// O SUBMENU de Personagens, Cenários e Figuras.
//
// T1.14 do pedido: "'Figuras' confuso". Abri o painel na vitrine e o ponto é literal — ele abria
// uma caixa bege VAZIA com um único botão "Gerar Imagem" e nada mais. Quatro coisas erradas ao
// mesmo tempo, e nenhuma delas aparece em typecheck:
//
//   1. NÃO DIZIA O QUE ERA. O mesmo painel serve a Personagens, Cenários e Figuras, e não tinha
//      título nenhum — aberto, os três são indistinguíveis.
//   2. NÃO TINHA ESTADO VAZIO. Sem imagem salva, o resultado é um retângulo bege. A criança não
//      tem como saber se está vazio, se quebrou, ou se ainda vai carregar.
//   3. `alt="Personagem"` EM TODA IMAGEM, inclusive nas de cenário e figura. Para quem usa leitor
//      de tela, uma prateleira de cenários é anunciada como uma fila de personagens.
//   4. `<img onClick>` — não chega por Tab, não dispara com Enter, sem estado de foco. A mesma
//      doença do resto do produto.

import "./submenu.scss";
import { O_QUE_GERA } from "./generate-image";
import { ImgInfo } from "../../api/app-user.class";
import { GetSet } from "../../types";
import { ElType } from "../../classes/comic.class";
import {
  daPrateleira as daPrateleiraDaConta,
  ehPrateleira,
} from "./prateleira-da-imagem";
import { EstiloDeBalao, urlDoBalao } from "../../classes/balao-do-estilo";
import { CENARIOS_PRONTOS } from "../../classes/cenarios-prontos";

type PropsType = {
  imgs: Record<string, ImgInfo>;
  SelectedSubmenu: GetSet<ElType | null>;
  addFunctions: Record<ElType, (src: any) => void>;
  openGenerateImg: () => any;
};

// `Extract` e não `ElType` inteiro: `rectangles` não é prateleira — o quadro se insere pelo
// "Inserir Quadro", com os próprios estilos, e listá-lo aqui seria inventar entrada para uma tela
// que não existe.
//
// `textBubbles` ENTROU EM 26/09 (`T8.9`): era o único dos quatro botões do menu que abria este
// painel sem título, sem opção nenhuma e com um "Gerar imagem" que não pertence ali.
type PrateleiraDeImagem = Extract<ElType, "characters" | "scenarios" | "figures" | "textBubbles">;

/**
 * O que cada prateleira é, em português, para a tela poder dizer.
 *
 * O texto do vazio não é um aviso de erro: é a única chance de explicar o que a categoria
 * significa. Quem abre "Figuras" pela primeira vez não sabe que figura é objeto solto — e
 * descobrir isso por tentativa é o que o T1.14 chama de confuso.
 */
/**
 * AS DICAS DE CADA PRATELEIRA — `T8.32`.
 *
 * O relatório, depois de percorrer o editor inteiro: *"Hoje várias dessas ações só se descobrem por
 * tentativa."* E ele traz as três dicas escritas:
 *
 *     "clique num balão para colocá-lo na página"
 *     "clique duas vezes para escrever"
 *     "clique na imagem gerada para guardá-la"
 *
 * ── POR QUE A DICA FICA AQUI E NÃO NUM TOUR DE BOAS-VINDAS ───────────────────────────────────
 *
 * Um tour aparece uma vez, antes de a pessoa ter o que fazer, e é esquecido. A dica de prateleira
 * aparece **no momento em que a ação é possível** e some quando a prateleira fecha — que é quando
 * ela deixa de servir. Custa uma linha de tela e não interrompe ninguém.
 *
 * E ela é DIFERENTE do texto de prateleira vazia (`vazio`): aquele diz como CONSEGUIR um item; este
 * diz o que fazer com o item que já está ali. Quem tem seis personagens e não sabe como pôr um na
 * página não é ajudado por "você ainda não tem personagens".
 */
const PRATELEIRA: Record<
  PrateleiraDeImagem,
  { titulo: string; vazio: string; umItem: string; dica: string }
> = {
  characters: {
    titulo: "Personagens",
    vazio:
      "Você ainda não tem personagens. Toque em “Gerar personagem” e descreva quem é — o nome, a " +
      "roupa, o jeito — que o HQ-Lab desenha.",
    umItem: "Personagem",
    dica: "Clique num personagem para colocá-lo no quadro escolhido.",
  },
  scenarios: {
    titulo: "Cenários",
    vazio:
      "Você ainda não tem cenários. Cenário é o LUGAR onde a cena acontece: a sala, o pátio, a " +
      "rua. Toque em “Gerar cenário” e descreva o lugar.",
    umItem: "Cenário",
    dica: "Clique num cenário para colocá-lo no quadro escolhido.",
  },
  figures: {
    titulo: "Figuras",
    vazio:
      "Você ainda não tem figuras. Figura é um objeto solto que entra na cena — uma bola, um " +
      "livro, uma árvore. Toque em “Gerar figura” e descreva o objeto.",
    umItem: "Figura",
    dica: "Clique numa figura para colocá-la no quadro escolhido.",
  },
  textBubbles: {
    titulo: "Textos",
    // Esta prateleira NUNCA está vazia: os cinco balões são desenhados, não gerados. O texto existe
    // porque o tipo pede um, e porque um dia pode haver balão salvo na conta e nenhum ainda.
    vazio: "Escolha a forma do balão abaixo e escreva o que vai dentro.",
    umItem: "Balão",
    // T8.32 · a segunda das tres dicas do relatorio — "clique duas vezes para escrever" — entrou
    // aqui no dia em que o gesto passou a existir (T8.10). Prometer numa dica um gesto que o editor
    // nao faz e pior que nao ter dica.
    dica:
      "Clique numa forma para pôr o balão no quadro escolhido — a caixa de escrever abre em " +
      "seguida. Depois, clique duas vezes no balão para escrever de novo.",
  },
};

/**
 * OS CINCO BALÕES QUE A PRATELEIRA "TEXTOS" OFERECE — `T8.9`.
 *
 * O ponto, na letra: *"Textos abre sem título, sem opções de texto e com um botão 'Gerar imagem',
 * que não faz sentido ali. (…) **Não encontrei no editor nenhuma forma evidente de escrever um texto
 * ou uma fala.**"* A última frase é o achado: o balão sempre foi inserível, e não havia por onde.
 *
 * ── DE ONDE VEM O DESENHO, E POR QUE NÃO É IMAGEM DE ACERVO ──────────────────────────────────
 *
 * As cinco formas já existem em `balao-do-estilo.ts`, em SVG, desde o `T6.5` — foram feitas para o
 * roteirizador, que grava o estilo de cada balão. Aqui elas viram opção de tela, e a razão de não
 * subir PNGs está escrita lá: o acervo de imagem é **por conta**, então cada criança precisaria da
 * própria cópia dos balões; e o armazenamento de arquivo está fora do plano de cobrança (`T6.6`,
 * HTTP 402). SVG é string, cabe num `data:` URI e entra pelo mesmo caminho de imagem que já existe.
 *
 * ── O NOME E A EXPLICAÇÃO DE CADA UM NÃO SÃO ENFEITE ─────────────────────────────────────────
 *
 * Em quadrinho a forma do balão é **gramática**: é ela que diz se alguém fala, pensa ou grita, e a
 * criança lê isso sem ninguém explicar. Uma paleta de cinco formas sem nome obrigaria a descobrir
 * por tentativa — que é literalmente a queixa do `T8.32`.
 */
const BALOES: { estilo: EstiloDeBalao; nome: string; oQueDiz: string }[] = [
  { estilo: "fala", nome: "Fala", oQueDiz: "Alguém está falando. O rabicho aponta quem." },
  { estilo: "pensamento", nome: "Pensamento", oQueDiz: "Está pensando, não falando." },
  { estilo: "grito", nome: "Grito", oQueDiz: "Está gritando." },
  { estilo: "sussurro", nome: "Sussurro", oQueDiz: "Está falando baixo." },
  { estilo: "narracao", nome: "Narração", oQueDiz: "É o narrador — não é de personagem nenhum." },
];

function Submenu({
  imgs,
  SelectedSubmenu,
  addFunctions,
  openGenerateImg,
}: PropsType): JSX.Element {
  const selectedSubmenu = SelectedSubmenu();
  const prateleira =
    selectedSubmenu && selectedSubmenu in PRATELEIRA
      ? PRATELEIRA[selectedSubmenu as PrateleiraDeImagem]
      : null;

  // ── A DECISAO SAIU DAQUI, E NAO E ESTETICA (T5.13) ────────────────────────────────────────
  //
  // Era `info.type === selectedSubmenu`, igualdade exata. Com ela, uma imagem cujo `type` nao
  // fosse um dos tres NAO APARECIA EM PRATELEIRA NENHUMA — e isso acontece de tres maneiras:
  // `saveImg` aceita `ElType`, que tem CINCO valores; documento antigo nao tem o campo; e o
  // roteirizador grava `type: "figures"` num objeto literal, onde uma letra trocada torna a
  // imagem invisivel sem erro nenhum.
  //
  // Ele, na letra: *"so algumas coisas, nao foi tudo que foi transferido (...) tem que transferir
  // tudo"*. A regra mora em `prateleira-da-imagem.ts`, com o teste que cobra o "tudo": somar as
  // tres prateleiras tem que dar o total do catalogo.
  const daPrateleira = selectedSubmenu && ehPrateleira(selectedSubmenu)
    ? daPrateleiraDaConta(imgs, selectedSubmenu)
    : [];

  return (
    <div
      style={{ display: !selectedSubmenu ? "none" : "flex" }}
      className="submenu-container"
      // O painel é uma região com nome: quem navega por teclado ou leitor de tela precisa saber
      // em qual das três prateleiras entrou.
      role="region"
      aria-label={prateleira?.titulo}
    >
      <div className="submenu-topo">
        <h3>{prateleira?.titulo}</h3>
        {/* T8.9 · "Gerar imagem" NAO aparece em Textos, e ele, na letra: "um botao 'Gerar imagem',
            que nao faz sentido ali". Nao faz: os cinco baloes sao desenhados aqui, nao gerados por
            IA, e nao ha o que descrever. Nas outras tres o botao continua, porque ali ele e o unico
            jeito de a prateleira deixar de estar vazia. */}
        {selectedSubmenu !== "textBubbles" && (
          // T24 · o botão diz o que a prateleira gera: «deveria ser gerar personagem».
          <button type="button" onClick={openGenerateImg}>
            {O_QUE_GERA[selectedSubmenu ?? ""] ?? "Gerar imagem"}
          </button>
        )}
      </div>

      {/* T8.32 · a dica aparece quando a acao e possivel, e so quando ha item para agir sobre.
          Com a prateleira vazia o texto de `vazio` ja diz o que fazer, e duas frases de instrucao
          ao mesmo tempo nao ajudam ninguem. */}
      {/* Em Textos a dica aparece SEMPRE: as cinco formas estao sempre ali, entao a acao esta
          sempre possivel — e a dica e a unica coisa na tela que diz que a caixa de escrever vem
          depois do clique. */}
      {prateleira?.dica && (daPrateleira.length > 0 || selectedSubmenu === "textBubbles" || selectedSubmenu === "scenarios") && (
        <p className="submenu-dica">{prateleira.dica}</p>
      )}

      <div className="submenu-content">
        {/* T8.9 · as cinco formas de balao. Ficam ANTES das imagens da conta porque sao as que
            sempre existem: uma prateleira que abre com opcao e uma prateleira que abre com uma
            frase de desculpa sao duas telas diferentes. */}
        {selectedSubmenu === "textBubbles" &&
          BALOES.map((b) => (
            <button
              key={b.estilo}
              type="button"
              className="submenu-item submenu-balao"
              title={b.oQueDiz}
              onClick={() => {
                addFunctions.textBubbles(b.estilo);
                SelectedSubmenu(null);
              }}
            >
              {/* O `alt` diz o nome E o que a forma significa: quem ouve a tela nao ve a forma, e
                  e a forma que carrega a gramatica do quadrinho. */}
              <img src={urlDoBalao(b.estilo)} alt={`${b.nome} — ${b.oQueDiz}`} />
              <span className="submenu-balao-nome">{b.nome}</span>
            </button>
          ))}

        {/* T11.6 · os seis cenarios prontos, antes dos da conta, pelo mesmo motivo dos baloes: a
            prateleira abre com opcao, e nao com uma frase de desculpa. */}
        {selectedSubmenu === "scenarios" &&
          CENARIOS_PRONTOS.map((c) => (
            <button
              key={c.id}
              type="button"
              className="submenu-item submenu-cenario-pronto"
              title={c.nome}
              onClick={() => {
                addFunctions.scenarios(c.id);
                SelectedSubmenu(null);
              }}
            >
              <img src={c.url} alt={`Cenário pronto — ${c.nome}`} loading="lazy" />
              <span className="submenu-balao-nome">{c.nome}</span>
            </button>
          ))}

        {daPrateleira.length === 0 && prateleira && selectedSubmenu !== "textBubbles" && selectedSubmenu !== "scenarios" && (
          <p className="submenu-vazio">{prateleira.vazio}</p>
        )}

        {daPrateleira.map(([id, imgInfo]) => (
          <button
            key={id}
            type="button"
            className="submenu-item"
            onClick={() => {
              if (!selectedSubmenu) return;
              addFunctions[selectedSubmenu](id);
              SelectedSubmenu(null);
            }}
          >
            <img
              src={imgInfo.url}
              // O `alt` acompanha a prateleira. Era `"Personagem"` fixo, em cenário e figura
              // também.
              alt={`${prateleira?.umItem ?? "Item"} — usar nesta página`}
            />
          </button>
        ))}
      </div>
    </div>
  );
}

export default Submenu;
